import { BlockBlobClient } from "@azure/storage-blob";
import type { UploadFile } from "antd/es/upload/interface";
import { axiosBase, errorHandler } from "./axios";
import { getAccessToken } from "./helper";
import magickWasmUrl from "@imagemagick/magick-wasm/magick.wasm?url";
import { io, type Socket } from "socket.io-client";
import {
    buildUploadResumeId,
    deleteUploadResumeRecord,
    getUploadResumeRecord,
    purgeStaleUploadResumeRecords,
    saveUploadResumeRecord,
    type UploadResumeRecord,
} from "./uploadResumeStore";

const SAS_UPLOAD_ENDPOINT = "/api/v1/media/upload";
const BLOCK_SIZE = 4 * 1024 * 1024;
const BLOCK_CONCURRENCY = 4;
const FILE_CONCURRENCY = 5;
const STALE_RESUME_RECORD_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface GenerateSasInput {
    studio: string;
    eventId: string;
    serviceId?: string | null;
    type?: "images" | "videos";
    totalFiles?: number;
}

interface SasPayload {
    uploadUrl: string;
    sasToken: string;
    sessionId?: string;
}

interface SasResponse {
    success?: boolean;
    message?: string;
    errorMessage?: string;
    resultObject?: SasPayload;
}

export interface ListedMediaItem {
    name: string;
    size: number;
    url?: string;
    thumbUrl?: string;
}

interface ListMediaResultObject {
    items?: ListedMediaItem[];
    nextContinuationToken?: string | null;
    hasMore?: boolean;
}

interface ListMediaResponse {
    success?: boolean;
    message?: string;
    errorMessage?: string;
    resultObject?: ListMediaResultObject;
}

interface AcknowledgeMediaInput {
    studioId: string;
    eventId: string;
    serviceId?: string | null;
    isSubmitted?: boolean;
    isAcknowledged?: boolean;
}

interface AcknowledgeMediaResultObject {
    mediaId?: string;
    eventId?: string;
    studioId?: string;
    serviceId?: string | null;
    isSubmitted?: boolean;
    submittedBy?: string | null;
    isAcknowledged?: boolean;
    acknowledgedBy?: string | null;
}

interface AcknowledgeMediaResponse {
    success?: boolean;
    message?: string;
    errorMessage?: string;
    resultObject?: AcknowledgeMediaResultObject;
}

interface ListMediaInput {
    studioId: string;
    eventId: string;
    serviceId?: string | null;
    type?: "images" | "videos" | "all";
    limit?: number;
    continuationToken?: string | null;
    continuationTokenImages?: string | null;
    continuationTokenVideos?: string | null;
    append?: boolean;
}

export interface UploadImagesProgress {
    fileName: string;
    loadedBytes: number;
    totalBytes: number;
    percentage: number;
}

export interface UploadImagesSuccessItem {
    fileName: string;
}

export interface UploadImagesFailedItem {
    fileName: string;
    error: string;
}

export interface UploadImagesResult {
    success: UploadImagesSuccessItem[];
    failed: UploadImagesFailedItem[];
}

interface UploadImagesOptions {
    onProgress?: (progress: UploadImagesProgress) => void;
}

type BrowserUploadInput = File | UploadFile;

const generateSasForUpload = (data: GenerateSasInput) => {
    return axiosBase
        .post(SAS_UPLOAD_ENDPOINT, data)
        .then((response) => response.data)
        .catch((error) => errorHandler(error));
};

const getRawFile = (file: BrowserUploadInput): File => {
    if (file instanceof File) {
        return file;
    }

    if (file.originFileObj instanceof File) {
        return file.originFileObj;
    }

    throw new Error(`Missing file data for ${file.name}`);
};

const getErrorMessage = (error: unknown) => {
    if (error instanceof Error) {
        return error.message;
    }

    return "Upload failed";
};

const normalizeUploadUrl = (uploadUrl: string) => uploadUrl.replace(/\/+$/, "");
const joinBlobPath = (...segments: string[]) => segments
    .map((segment, index) => index === 0 ? segment.replace(/\/+$/, "") : segment.replace(/^\/+|\/+$/g, ""))
    .filter(Boolean)
    .join("/");
const SOCKET_HEARTBEAT_INTERVAL_MS = 10000;

const getSafeLastModified = (file: File) => Number.isFinite(file.lastModified) ? file.lastModified : 0;

const getTotalBlockCount = (fileSize: number, blockSize: number) => (
    fileSize <= 0 ? 0 : Math.ceil(fileSize / blockSize)
);

const getBlockBounds = (blockIndex: number, blockSize: number, fileSize: number) => {
    const start = blockIndex * blockSize;
    const end = Math.min(fileSize, start + blockSize);

    return {
        start,
        end,
        length: Math.max(0, end - start),
    };
};

const getBlockId = (blockIndex: number) => btoa(`block-${String(blockIndex).padStart(8, "0")}`);

const normalizeCompletedBlockIndexes = (completedBlockIndexes: number[], totalBlocks: number) => (
    Array.from(new Set(
        (completedBlockIndexes || []).filter((index) => Number.isInteger(index) && index >= 0 && index < totalBlocks),
    )).sort((left, right) => left - right)
);

const getUploadedBytesForBlocks = (completedBlockIndexes: number[], fileSize: number, blockSize: number) => (
    completedBlockIndexes.reduce((total, blockIndex) => total + getBlockBounds(blockIndex, blockSize, fileSize).length, 0)
);

const decodeBlobFileName = (fileName?: string) => {
    if (!fileName) {
        return "";
    }

    try {
        return decodeURIComponent(fileName);
    } catch {
        return fileName;
    }
};

const normalizeListedMediaItem = (item: ListedMediaItem): ListedMediaItem => ({
    ...item,
    name: decodeBlobFileName(item.name),
});

const createUploadSocket = (): Socket | null => {
    const token = getAccessToken();
    const socketUrl = typeof axiosBase.defaults.baseURL === "string" && axiosBase.defaults.baseURL
        ? axiosBase.defaults.baseURL
        : window.location.origin;

    if (!token) {
        return null;
    }

    try {
        return io(socketUrl, {
            transports: ["websocket"],
            auth: { token },
            reconnection: true,
        });
    } catch {
        return null;
    }
};

const createResumeRecord = ({
    studio,
    eventId,
    serviceId,
    mediaType,
    file,
    blockSize,
    blobName,
    totalBlocks,
}: {
    studio: string;
    eventId: string;
    serviceId?: string | null;
    mediaType: "images" | "videos";
    file: File;
    blockSize: number;
    blobName: string;
    totalBlocks: number;
}): UploadResumeRecord => {
    const lastModified = getSafeLastModified(file);

    return {
        id: buildUploadResumeId({
            studioId: studio,
            eventId,
            serviceId,
            mediaType,
            fileName: file.name,
            fileSize: file.size,
            lastModified,
        }),
        studioId: studio,
        eventId,
        serviceId,
        mediaType,
        fileName: file.name,
        fileSize: file.size,
        lastModified,
        blobName,
        blockSize,
        totalBlocks,
        completedBlockIndexes: [],
        updatedAt: Date.now(),
    };
};

const isCompatibleResumeRecord = (
    record: UploadResumeRecord | null,
    expected: Pick<UploadResumeRecord, "blobName" | "blockSize" | "totalBlocks" | "fileSize" | "lastModified">,
) => Boolean(
    record
    && record.blobName === expected.blobName
    && record.blockSize === expected.blockSize
    && record.totalBlocks === expected.totalBlocks
    && record.fileSize === expected.fileSize
    && record.lastModified === expected.lastModified
);

const isResumeStateInvalidError = (error: unknown) => {
    const code = String((error as any)?.details?.errorCode || (error as any)?.code || "");
    const message = String((error as any)?.message || "");
    const combined = `${code} ${message}`;

    return /InvalidBlock|InvalidBlockList|InvalidBlobOrBlock|BlobNotFound|BlockCountExceedsLimit/i.test(combined);
};

const uploadBlockBlobResumable = async ({
    rawFile,
    blockBlobClient,
    resumeRecord,
    options,
}: {
    rawFile: File;
    blockBlobClient: BlockBlobClient;
    resumeRecord: UploadResumeRecord;
    options?: UploadImagesOptions;
}) => {
    const completedBlocks = new Set<number>(resumeRecord.completedBlockIndexes);
    const activeBlockProgress = new Map<number, number>();
    let uploadedBytes = getUploadedBytesForBlocks(resumeRecord.completedBlockIndexes, rawFile.size, resumeRecord.blockSize);
    let persistQueue = Promise.resolve();

    const emitProgress = () => {
        const inFlightBytes = Array.from(activeBlockProgress.values()).reduce((total, value) => total + value, 0);
        const loadedBytes = Math.min(rawFile.size, uploadedBytes + inFlightBytes);
        const isComplete = rawFile.size > 0 && loadedBytes >= rawFile.size;

        options?.onProgress?.({
            fileName: rawFile.name,
            loadedBytes,
            totalBytes: rawFile.size,
            percentage: rawFile.size
                ? (isComplete ? 99 : Math.min(99, Math.round((loadedBytes / rawFile.size) * 100)))
                : 0,
        });
    };

    const persistCompletedBlocks = () => {
        const nextRecord: UploadResumeRecord = {
            ...resumeRecord,
            completedBlockIndexes: normalizeCompletedBlockIndexes(Array.from(completedBlocks), resumeRecord.totalBlocks),
            updatedAt: Date.now(),
        };

        persistQueue = persistQueue.then(() => saveUploadResumeRecord(nextRecord));
        return persistQueue;
    };

    emitProgress();

    const pendingBlockIndexes = Array.from({ length: resumeRecord.totalBlocks }, (_, index) => index)
        .filter((blockIndex) => !completedBlocks.has(blockIndex));

    if (pendingBlockIndexes.length) {
        let nextPendingIndex = 0;

        const stageNextBlock = async () => {
            while (nextPendingIndex < pendingBlockIndexes.length) {
                const blockIndex = pendingBlockIndexes[nextPendingIndex];
                nextPendingIndex += 1;

                const { start, end, length } = getBlockBounds(blockIndex, resumeRecord.blockSize, rawFile.size);
                const blockBlob = rawFile.slice(start, end);
                activeBlockProgress.set(blockIndex, 0);
                emitProgress();

                try {
                    await blockBlobClient.stageBlock(getBlockId(blockIndex), blockBlob, length, {
                        onProgress: ({ loadedBytes }) => {
                            activeBlockProgress.set(blockIndex, loadedBytes);
                            emitProgress();
                        },
                    });
                } finally {
                    activeBlockProgress.delete(blockIndex);
                }

                completedBlocks.add(blockIndex);
                uploadedBytes += length;
                emitProgress();
                await persistCompletedBlocks();
            }
        };

        const workers = Array.from(
            { length: Math.min(BLOCK_CONCURRENCY, pendingBlockIndexes.length) },
            () => stageNextBlock(),
        );

        await Promise.all(workers);
    }

    await persistQueue;

    await blockBlobClient.commitBlockList(
        Array.from({ length: resumeRecord.totalBlocks }, (_, index) => getBlockId(index)),
        {
            blobHTTPHeaders: {
                blobContentType: rawFile.type || "application/octet-stream",
                blobCacheControl: "public, max-age=31536000",
            },
        },
    );
};

/** All camera RAW file extensions – must stay in sync with MediaAcknowledgment. */
const RAW_EXTENSIONS = new Set([
    ".3fr", ".arw", ".cr2", ".cr3", ".dcr", ".dng", ".erf", ".fff",
    ".iiq", ".k25", ".kdc", ".mef", ".mos", ".mrw", ".nef", ".nrw",
    ".orf", ".pef", ".raf", ".raw", ".rw2", ".rwl", ".sr2", ".srf",
    ".srw", ".x3f",
]);

const isRawFile = (fileName: string): boolean => {
    const lower = fileName.toLowerCase();
    const lastDot = lower.lastIndexOf(".");
    return lastDot !== -1 && RAW_EXTENSIONS.has(lower.slice(lastDot));
};

// ─── Lazy ImageMagick WASM initialisation ─────────────────────────────────────
// Only initialised on demand (first RAW thumbnail request) to avoid paying the
// ~10 MB WASM load cost for uploads that contain no RAW files.

let _magickInitPromise: Promise<void> | null = null;

const ensureMagickInitialised = (): Promise<void> => {
    if (_magickInitPromise) return _magickInitPromise;
    _magickInitPromise = import("@imagemagick/magick-wasm")
        .then(({ initializeImageMagick }) =>
            initializeImageMagick(new URL(magickWasmUrl, location.href))
        )
        .catch((err) => {
            // Reset so a subsequent call can retry.
            _magickInitPromise = null;
            throw err;
        });
    return _magickInitPromise;
};

/**
 * Generate a WebP thumbnail from a camera RAW file using ImageMagick WASM.
 * Returns `null` on any failure so the main upload is never blocked.
 */
const generateRawImageThumbnailWithMagick = async (
    file: File,
    maxWidth = 800,
): Promise<Blob | null> => {
    try {
        await ensureMagickInitialised();
        const { ImageMagick, MagickFormat } = await import("@imagemagick/magick-wasm");

        const bytes = new Uint8Array(await file.arrayBuffer());

        return ImageMagick.read(bytes, (image): Blob | null => {
            try {
                const width = Math.min(maxWidth, image.width);
                const height = Math.round(width * (image.height / image.width));
                image.resize(width, height);
                return image.write(MagickFormat.WebP, (data) => {
                        // Copy into a plain ArrayBuffer so Blob() accepts it (data.buffer is ArrayBufferLike)
                        const ab = new ArrayBuffer(data.byteLength);
                        new Uint8Array(ab).set(data);
                        return new Blob([ab], { type: "image/webp" });
                    });
            } catch {
                return null;
            }
        });
    } catch {
        return null;
    }
};

const generateImageThumbnail = async (file: File, maxWidth = 800): Promise<Blob | null> => {
    try {
        const imageBitmap = await createImageBitmap(file);
        const ratio = imageBitmap.height / imageBitmap.width;
        const width = Math.min(maxWidth, imageBitmap.width);
        const height = Math.round(width * ratio);

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        ctx.drawImage(imageBitmap, 0, 0, width, height);

        return await new Promise<Blob | null>((resolve) => {
            canvas.toBlob((blob) => resolve(blob), "image/webp", 0.8);
        });
    } catch (e) {
        return null;
    }
};

const generateVideoThumbnail = async (file: File, maxWidth = 640, seekSeconds = 1): Promise<Blob | null> => {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement("video");
        let resolved = false;

        const cleanup = () => {
            try { video.pause(); } catch {}
            URL.revokeObjectURL(url);
        };

        video.preload = "metadata";
        video.muted = true;
        video.playsInline = true;
        video.src = url;

        video.addEventListener("loadedmetadata", () => {
            const duration = isFinite(video.duration) ? video.duration : 0;
            video.currentTime = Math.min(seekSeconds, Math.max(0, duration / 2));
        });

        video.addEventListener("seeked", async () => {
            try {
                const ratio = video.videoHeight / video.videoWidth || 1;
                const width = Math.min(maxWidth, video.videoWidth || maxWidth);
                const height = Math.round(width * ratio);

                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                if (!ctx) throw new Error("No canvas context");
                ctx.drawImage(video, 0, 0, width, height);
                canvas.toBlob((blob) => {
                    cleanup();
                    resolved = true;
                    resolve(blob);
                }, "image/webp", 0.8);
            } catch (err) {
                cleanup();
                resolved = true;
                resolve(null);
            }
        });

        video.addEventListener("error", () => {
            if (!resolved) {
                cleanup();
                resolve(null);
            }
        });

        // Kick off loading
        video.load();
    });
};

const uploadSingleImage = async (
    file: BrowserUploadInput,
    sasPayload: SasPayload,
    options?: UploadImagesOptions,
    context?: { studio: string; eventId: string; serviceId?: string | null; mediaType: "images" | "videos" },
): Promise<UploadImagesSuccessItem> => {
    const rawFile = getRawFile(file);
    const cleanUploadUrl = normalizeUploadUrl(sasPayload.uploadUrl);
    const blobName = rawFile.name;
    const blobUrl = joinBlobPath(cleanUploadUrl, "original", encodeURIComponent(blobName));
    const blockBlobClient = new BlockBlobClient(`${blobUrl}?${sasPayload.sasToken}`);
    const totalBlocks = getTotalBlockCount(rawFile.size, BLOCK_SIZE);
    const baseResumeRecord = context
        ? createResumeRecord({
            studio: context.studio,
            eventId: context.eventId,
            serviceId: context.serviceId,
            mediaType: context.mediaType,
            file: rawFile,
            blockSize: BLOCK_SIZE,
            blobName,
            totalBlocks,
        })
        : null;

    // Generate a thumbnail for every supported file type:
    //   – RAW images  → ImageMagick WASM (handles all camera RAW formats)
    //   – Other images → fast Canvas-based generator
    //   – Videos       → canvas frame grab
    let thumbnailBlob: Blob | null = null;
    try {
        if (rawFile.type.startsWith("video/")) {
            thumbnailBlob = await generateVideoThumbnail(rawFile as File);
        } else if (isRawFile(rawFile.name)) {
            thumbnailBlob = await generateRawImageThumbnailWithMagick(rawFile as File, 800);
        } else if (rawFile.type.startsWith("image/") || rawFile.type === "") {
            thumbnailBlob = await generateImageThumbnail(rawFile as File, 800);
        }
    } catch {
        thumbnailBlob = null;
    }

    // Seed progress immediately so the UI can switch into uploading state.
    options?.onProgress?.({
        fileName: rawFile.name,
        loadedBytes: 0,
        totalBytes: rawFile.size,
        percentage: 0,
    });

    if (rawFile.size <= 0) {
        await blockBlobClient.uploadData(rawFile, {
            blobHTTPHeaders: {
                blobContentType: rawFile.type || "application/octet-stream",
                blobCacheControl: "public, max-age=31536000",
            },
        });

        if (baseResumeRecord) {
            await deleteUploadResumeRecord(baseResumeRecord.id);
        }
    } else if (baseResumeRecord) {
        const storedRecord = await getUploadResumeRecord(baseResumeRecord.id);
        const activeResumeRecord = isCompatibleResumeRecord(storedRecord, {
            blobName,
            blockSize: BLOCK_SIZE,
            totalBlocks,
            fileSize: rawFile.size,
            lastModified: getSafeLastModified(rawFile),
        })
            ? {
                ...storedRecord,
                completedBlockIndexes: normalizeCompletedBlockIndexes(storedRecord?.completedBlockIndexes || [], totalBlocks),
            }
            : baseResumeRecord;

        if (activeResumeRecord === baseResumeRecord) {
            await saveUploadResumeRecord(baseResumeRecord);
        }

        try {
            await uploadBlockBlobResumable({
                rawFile,
                blockBlobClient,
                resumeRecord: activeResumeRecord,
                options,
            });
        } catch (error) {
            if (activeResumeRecord.completedBlockIndexes.length > 0 && isResumeStateInvalidError(error)) {
                await deleteUploadResumeRecord(activeResumeRecord.id);
                await saveUploadResumeRecord(baseResumeRecord);
                await uploadBlockBlobResumable({
                    rawFile,
                    blockBlobClient,
                    resumeRecord: baseResumeRecord,
                    options,
                });
            } else {
                throw error;
            }
        }

        await deleteUploadResumeRecord(baseResumeRecord.id);
    } else {
        await blockBlobClient.uploadBrowserData(rawFile, {
            blockSize: BLOCK_SIZE,
            concurrency: BLOCK_CONCURRENCY,
            blobHTTPHeaders: {
                blobContentType: rawFile.type || "application/octet-stream",
                blobCacheControl: "public, max-age=31536000"
            },
            onProgress: ({ loadedBytes }) => {
                options?.onProgress?.({
                    fileName: rawFile.name,
                    loadedBytes,
                    totalBytes: rawFile.size,
                    percentage: rawFile.size
                        ? Math.min(99, Math.round((loadedBytes / rawFile.size) * 100))
                        : 0,
                });
            },
        });
    }

    // Force 100% — small files finish in one request and onProgress never fires.
    options?.onProgress?.({
        fileName: rawFile.name,
        loadedBytes: rawFile.size,
        totalBytes: rawFile.size,
        percentage: 100,
    });

    // Upload thumbnail into the sibling `thumbnail` subfolder in the same
    // container so the same SAS token can be reused for both assets.
    if (thumbnailBlob) {
        try {
            const thumbFileName = `${rawFile.name}.webp`;
            const thumbBlobUrl = joinBlobPath(cleanUploadUrl, "thumbnail", encodeURIComponent(thumbFileName));
            const thumbClient = new BlockBlobClient(`${thumbBlobUrl}?${sasPayload.sasToken}`);
            await thumbClient.uploadData(thumbnailBlob, {
                blobHTTPHeaders: {
                    blobContentType: "image/webp",
                    blobCacheControl: "public, max-age=31536000",
                },
            });
        } catch {
            // Swallow – thumbnail upload is best-effort only.
        }
    }

    return {
        fileName: rawFile.name
    };
};

const uploadImages = async (
    files: BrowserUploadInput[],
    studio: string,
    eventId: string,
    serviceId: string | null = null,
    options?: UploadImagesOptions,
    mediaType: "images" | "videos" = "images",
): Promise<UploadImagesResult> => {
    const success: UploadImagesSuccessItem[] = [];
    const failed: UploadImagesFailedItem[] = [];

    if (!files.length) {
        return { success, failed };
    }

    void purgeStaleUploadResumeRecords(STALE_RESUME_RECORD_TTL_MS);

    const response = await generateSasForUpload({
        studio,
        eventId,
        serviceId,
        type: mediaType,
        totalFiles: files.length,
    }) as SasResponse;
    const sasPayload = response?.resultObject;

    if (!response?.success || !sasPayload?.uploadUrl || !sasPayload?.sasToken) {
        throw new Error(response?.message || response?.errorMessage || "Unable to generate upload link");
    }

    const socket = sasPayload.sessionId ? createUploadSocket() : null;
    let uploadedCount = 0;
    let failedCount = 0;

    const emitUploadTracking = (
        eventName: "upload:join" | "upload:heartbeat" | "upload:complete",
        waitForAck = false,
    ): Promise<void> => {
        if (!socket || !sasPayload.sessionId) {
            return Promise.resolve();
        }

        const payload = {
            sessionId: sasPayload.sessionId,
            studioId: studio,
            eventId,
            mediaType,
            totalFiles: files.length,
            uploadedCount,
            failedCount,
        };

        if (!waitForAck) {
            socket.emit(eventName, payload);
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            let settled = false;
            const finalize = () => {
                if (!settled) {
                    settled = true;
                    resolve();
                }
            };

            const timeoutId = window.setTimeout(finalize, 2000);
            socket.emit(eventName, payload, () => {
                window.clearTimeout(timeoutId);
                finalize();
            });
        });
    };

    void emitUploadTracking("upload:join");
    const heartbeatTimer = window.setInterval(() => {
        void emitUploadTracking("upload:heartbeat");
    }, SOCKET_HEARTBEAT_INTERVAL_MS);

    let currentIndex = 0;

    const worker = async () => {
        while (currentIndex < files.length) {
            const fileIndex = currentIndex;
            currentIndex += 1;
            const currentFile = files[fileIndex];
            const rawFile = getRawFile(currentFile);

            try {
                const uploadedFile = await uploadSingleImage(currentFile, sasPayload, options, {
                    studio,
                    eventId,
                    serviceId,
                    mediaType,
                });
                success.push(uploadedFile);
                uploadedCount += 1;
            } catch (error) {
                failed.push({
                    fileName: rawFile.name,
                    error: getErrorMessage(error),
                });
                failedCount += 1;
            }
        }
    };

    const workers = Array.from(
        { length: Math.min(FILE_CONCURRENCY, files.length) },
        () => worker(),
    );

    try {
        await Promise.all(workers);
        await emitUploadTracking("upload:complete", true);
    } finally {
        window.clearInterval(heartbeatTimer);
        socket?.disconnect();
    }

    return { success, failed };
};

const listMedia = (input: ListMediaInput): Promise<ListMediaResponse> => {
    return axiosBase
        .post("/api/v1/media/list", input)
        .then((response) => {
            const data = response.data as ListMediaResponse;

            if (Array.isArray(data?.resultObject?.items)) {
                data.resultObject = {
                    ...data.resultObject,
                    items: data.resultObject.items.map(normalizeListedMediaItem),
                };
            }

            return data;
        })
        .catch((error) => errorHandler(error));
};

const listAllMedia = async (input: ListMediaInput): Promise<ListedMediaItem[]> => {
    const items: ListedMediaItem[] = [];
    let continuationToken = input.continuationToken || null;

    do {
        const response = await listMedia({
            ...input,
            continuationToken,
            append: false,
        });

        if (!response?.success) {
            throw new Error(response?.message || response?.errorMessage || "Failed to fetch media list");
        }

        const resultObject = response.resultObject || {};
        items.push(...(resultObject.items || []));
        continuationToken = resultObject.nextContinuationToken || null;
    } while (continuationToken);

    return items;
};

const acknowledgeMedia = (input: AcknowledgeMediaInput): Promise<AcknowledgeMediaResponse> => {
    return axiosBase
    .post("/api/v1/media/media-status", input)
        .then((response) => response.data)
        .catch((error) => errorHandler(error));
};

export default {
    generateSasForUpload,
    uploadImages,
    listMedia,
    listAllMedia,
    acknowledgeMedia,
};